import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  LineChart, 
  PieChart, 
  ArrowUpRight, 
  ArrowDownRight, 
  CalendarDays, 
  Filter, 
  Download, 
  RefreshCw,
  ChevronDown,
  ListFilter,
  TrendingUp,
  TrendingDown,
  MapPin
} from 'lucide-react';
import { getHistoricalData, getStoresData, getRegionsData } from '../../data/mockData';

// Placeholder Chart Component
const ChartPlaceholder = ({ type, title, height = 300 }) => {
  const getChartColor = () => {
    switch (type) {
      case 'bar':
        return 'bg-blue-500';
      case 'line':
        return 'bg-emerald-500';
      case 'pie':
        return 'bg-amber-500';
      default:
        return 'bg-gray-500';
    }
  };

  const renderBars = () => {
    switch (type) {
      case 'bar':
        return (
          <div className="flex items-end h-full space-x-2 px-4">
            {[65, 80, 45, 70, 85, 60, 90, 75, 50, 65, 70, 85].map((height, i) => (
              <div 
                key={i} 
                className="flex-1 bg-blue-500 rounded-t-sm"
                style={{ height: `${height}%` }}
              />
            ))}
          </div>
        );
      case 'line':
        return (
          <div className="relative h-full w-full">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-px bg-gray-200"></div>
            </div>
            <div className="absolute inset-0 flex items-center" style={{ top: '25%' }}>
              <div className="w-full h-px bg-gray-200"></div>
            </div>
            <div className="absolute inset-0 flex items-center" style={{ top: '50%' }}>
              <div className="w-full h-px bg-gray-200"></div>
            </div>
            <div className="absolute inset-0 flex items-center" style={{ top: '75%' }}>
              <div className="w-full h-px bg-gray-200"></div>
            </div>
            <svg className="absolute inset-0" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path 
                d="M0,70 L8.33,65 L16.66,75 L25,60 L33.33,45 L41.66,55 L50,40 L58.33,30 L66.66,35 L75,20 L83.33,15 L91.66,25 L100,10" 
                fill="none"
                stroke="#10b981"
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
              <circle cx="0" cy="70" r="1.5" fill="#10b981" />
              <circle cx="8.33" cy="65" r="1.5" fill="#10b981" />
              <circle cx="16.66" cy="75" r="1.5" fill="#10b981" />
              <circle cx="25" cy="60" r="1.5" fill="#10b981" />
              <circle cx="33.33" cy="45" r="1.5" fill="#10b981" />
              <circle cx="41.66" cy="55" r="1.5" fill="#10b981" />
              <circle cx="50" cy="40" r="1.5" fill="#10b981" />
              <circle cx="58.33" cy="30" r="1.5" fill="#10b981" />
              <circle cx="66.66" cy="35" r="1.5" fill="#10b981" />
              <circle cx="75" cy="20" r="1.5" fill="#10b981" />
              <circle cx="83.33" cy="15" r="1.5" fill="#10b981" />
              <circle cx="91.66" cy="25" r="1.5" fill="#10b981" />
              <circle cx="100" cy="10" r="1.5" fill="#10b981" />
            </svg>
          </div>
        );
      case 'pie':
        return (
          <div className="flex items-center justify-center h-full">
            <div className="relative h-4/5 w-4/5 rounded-full overflow-hidden">
              <div className="absolute inset-0 bg-amber-500" style={{ clipPath: 'polygon(50% 50%, 0 0, 50% 0, 50% 50%)' }}></div>
              <div className="absolute inset-0 bg-blue-500" style={{ clipPath: 'polygon(50% 50%, 50% 0, 100% 0, 100% 100%, 50% 100%, 50% 50%)' }}></div>
              <div className="absolute inset-0 bg-emerald-500" style={{ clipPath: 'polygon(50% 50%, 50% 100%, 0 100%, 0 0, 0% 50%, 50% 50%)' }}></div>
              <div className="absolute inset-0 border-4 border-white rounded-full"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white h-1/3 w-1/3 rounded-full shadow-sm"></div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center">
        <div className="flex items-center">
          <div className={`p-2 rounded-lg ${type === 'bar' ? 'bg-blue-100' : type === 'line' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            {type === 'bar' ? (
              <BarChart3 className={`h-5 w-5 ${type === 'bar' ? 'text-blue-600' : type === 'line' ? 'text-emerald-600' : 'text-amber-600'}`} />
            ) : type === 'line' ? (
              <LineChart className={`h-5 w-5 ${type === 'bar' ? 'text-blue-600' : type === 'line' ? 'text-emerald-600' : 'text-amber-600'}`} />
            ) : (
              <PieChart className={`h-5 w-5 ${type === 'bar' ? 'text-blue-600' : type === 'line' ? 'text-emerald-600' : 'text-amber-600'}`} />
            )}
          </div>
          <h3 className="ml-3 text-lg font-medium text-gray-900">{title}</h3>
        </div>

        <div className="flex items-center space-x-2">
          <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <Filter className="h-4 w-4 text-gray-500" />
          </button>
          <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <Download className="h-4 w-4 text-gray-500" />
          </button>
        </div>
      </div>
      <div style={{ height: `${height}px` }} className="p-4">
        {renderBars()}
      </div>
    </div>
  );
};

// KPI Card Component
const KpiCard = ({ title, value, change, previousValue, icon: Icon }) => {
  const isPositive = change > 0;
  
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between">
        <div className="p-2 rounded-lg bg-blue-50">
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
        <div className={`flex items-center text-sm font-medium ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
          {isPositive ? (
            <ArrowUpRight className="h-4 w-4 mr-1" />
          ) : (
            <ArrowDownRight className="h-4 w-4 mr-1" />
          )}
          {Math.abs(change)}%
        </div>
      </div>
      <div className="mt-3">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        <p className="text-xs text-gray-500 mt-1">Previous: {previousValue}</p>
      </div>
    </div>
  );
};

// Top Performer Card Component
const TopPerformerCard = ({ title, stores }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full">
      <div className="p-4 border-b border-gray-100">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      </div>
      <div className="divide-y divide-gray-100">
        {stores.map((store, idx) => (
          <div key={store.id} className="flex items-center p-3 hover:bg-gray-50 transition-colors">
            <div className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center font-medium bg-blue-100 text-blue-800">
              {idx + 1}
            </div>
            <div className="ml-3 flex-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900">{store.name}</p>
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  store.score >= 85 ? 'bg-emerald-100 text-emerald-800' : 
                  store.score >= 70 ? 'bg-amber-100 text-amber-800' : 
                  'bg-rose-100 text-rose-800'
                }`}>
                  {store.score}
                </div>
              </div>
              <p className="text-xs text-gray-500">{store.region}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Compare Card Component
const CompareCard = ({ title, currentPeriod, previousPeriod, change, trend }) => {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <div className="mt-2 flex items-baseline justify-between">
        <p className="text-2xl font-bold text-gray-900">{currentPeriod}</p>
        <div className={`flex items-center text-sm font-medium ${
          trend === 'up' ? 'text-emerald-600' : 'text-rose-600'
        }`}>
          {trend === 'up' ? (
            <TrendingUp className="h-4 w-4 mr-1" />
          ) : (
            <TrendingDown className="h-4 w-4 mr-1" />
          )}
          {change}
        </div>
      </div>
      <div className="mt-2 text-xs text-gray-500">
        Previous: {previousPeriod}
      </div>
    </div>
  );
};

const Analytics = ({ period, region, setRegionFilter, setPeriodFilter }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [comparison, setComparison] = useState('previous');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [topStores, setTopStores] = useState([]);
  const [regions, setRegions] = useState([]);
  
  // Period options
  const periodOptions = [
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' },
    { value: 'year', label: 'Yearly' }
  ];
  
  // Comparison options
  const comparisonOptions = [
    { value: 'previous', label: 'vs Previous Period' },
    { value: 'year', label: 'vs Same Period Last Year' },
    { value: 'target', label: 'vs Target' }
  ];
  
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      // Fetch data
      const historicalData = await getHistoricalData(period, comparison);
      const stores = await getStoresData(period, region);
      const regionsData = await getRegionsData();
      
      // Sort stores by score
      const sortedStores = [...stores].sort((a, b) => b.totalScore - a.totalScore);
      
      setAnalyticsData(historicalData);
      setTopStores(sortedStores.slice(0, 5));
      setRegions(regionsData);
      setIsLoading(false);
    };
    
    loadData();
  }, [period, comparison, region]);
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        <span className="ml-2 text-gray-600">Loading analytics data...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative inline-block text-left">
          <select
            value={period}
            onChange={(e) => setPeriodFilter(e.target.value)}
            className="appearance-none pl-9 pr-10 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          >
            {periodOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <CalendarDays className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>

        <div className="relative inline-block text-left">
          <select
            value={comparison}
            onChange={(e) => setComparison(e.target.value)}
            className="appearance-none pl-9 pr-10 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          >
            {comparisonOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ListFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>

        <div className="relative inline-block text-left">
          <select
            value={region}
            onChange={(e) => setRegionFilter(e.target.value)}
            className="appearance-none pl-9 pr-10 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          >
            <option value="all">All Regions</option>
            {regions.map(region => (
              <option key={region.id} value={region.id}>
                {region.name} Region
              </option>
            ))}
          </select>
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        </div>

        <div className="ml-auto">
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
            <Download className="h-4 w-4" />
            Export Analytics
          </button>
        </div>
      </div>

      {/* Period Performance */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <CompareCard 
          title="Total Score"
          currentPeriod="78.5"
          previousPeriod="76.2"
          change="+3.0%"
          trend="up"
        />
        <CompareCard 
          title="Sales Performance"
          currentPeriod="24.8/30"
          previousPeriod="23.1/30"
          change="+7.3%"
          trend="up"
        />
        <CompareCard 
          title="Google Reviews"
          currentPeriod="16.5/20"
          previousPeriod="15.8/20"
          change="+4.4%"
          trend="up"
        />
        <CompareCard 
          title="Service Speed"
          currentPeriod="11.2/15"
          previousPeriod="11.9/15"
          change="-5.8%"
          trend="down"
        />
      </div>

      {/* Main Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartPlaceholder type="line" title="Performance Trend" />
        <ChartPlaceholder type="bar" title="Score By Category" />
      </div>

      {/* Secondary Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartPlaceholder type="pie" title="Score Distribution" height={250} />
        <ChartPlaceholder type="bar" title="Regional Comparison" height={250} />
        <ChartPlaceholder type="line" title="Year over Year" height={250} />
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Performers */}
        <TopPerformerCard title="Top Performing Stores" stores={topStores} />
        
        {/* Most Improved */}
        <TopPerformerCard 
          title="Most Improved Stores" 
          stores={[
            { id: 'S042', name: 'Khamis Mushait Mall', region: 'Southern Region', score: 74.2 },
            { id: 'S038', name: 'Al Khobar Corniche', region: 'Eastern Region', score: 82.7 },
            { id: 'S056', name: 'Makkah Grand Avenue', region: 'Western Region', score: 79.5 },
            { id: 'S012', name: 'Qassim University', region: 'Central Region', score: 85.2 },
            { id: 'S027', name: 'Jubail Industrial', region: 'Eastern Region', score: 76.3 }
          ]} 
        />
        
        {/* KPIs */}
        <div className="space-y-4">
          <KpiCard 
            title="Avg. Daily Sales" 
            value="SAR 32,450" 
            previousValue="SAR 28,750"
            change={12.8}
            icon={TrendingUp}
          />
          <KpiCard 
            title="Avg. Google Rating" 
            value="4.35/5" 
            previousValue="4.22/5"
            change={3.1}
            icon={TrendingUp}
          />
          <KpiCard 
            title="Service Speed" 
            value="145s" 
            previousValue="160s"
            change={9.4}
            icon={TrendingUp}
          />
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Score Breakdown by Category</h2>
          <p className="text-sm text-gray-500 mt-1">Current vs Previous Period</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Previous
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Change
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Max
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[
                { 
                  name: 'Sales Performance', 
                  current: 24.8, 
                  previous: 23.1, 
                  change: 7.3, 
                  target: 25.5, 
                  max: 30 
                },
                { 
                  name: 'Google Reviews', 
                  current: 16.5, 
                  previous: 15.8, 
                  change: 4.4, 
                  target: 17.0, 
                  max: 20 
                },
                { 
                  name: 'Maintenance', 
                  current: 8.2, 
                  previous: 7.8, 
                  change: 5.1, 
                  target: 8.5, 
                  max: 10 
                },
                { 
                  name: 'Audit Score', 
                  current: 8.5, 
                  previous: 7.9, 
                  change: 7.6, 
                  target: 8.5, 
                  max: 10 
                },
                { 
                  name: 'Speed of Service', 
                  current: 11.2, 
                  previous: 11.9, 
                  change: -5.8, 
                  target: 12.5, 
                  max: 15 
                },
                { 
                  name: 'Quality Control', 
                  current: 12.3, 
                  previous: 11.8, 
                  change: 4.2, 
                  target: 13.0, 
                  max: 15 
                },
                { 
                  name: 'Overall Score', 
                  current: 78.5, 
                  previous: 76.2, 
                  change: 3.0, 
                  target: 85.0, 
                  max: 100 
                }
              ].map((category, idx) => (
                <tr key={idx} className={idx === 6 ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{category.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                    {category.current.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {category.previous.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`flex items-center text-sm font-medium ${
                      category.change > 0 ? 'text-emerald-600' : 'text-rose-600'
                    }`}>
                      {category.change > 0 ? (
                        <TrendingUp className="h-4 w-4 mr-1" />
                      ) : (
                        <TrendingDown className="h-4 w-4 mr-1" />
                      )}
                      {Math.abs(category.change).toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {category.target.toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {category.max}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 